import { useState, useMemo } from "react";
import { useOrgSettings } from "@/lib/org-settings-context";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Receipt,
  Search,
  TrendingUp,
  Wallet,
  CreditCard,
  Clock,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ── Source types ──────────────────────────────────────────

type TransactionSource = "CARD" | "WALLET" | "TOPUP";

// ── Raw API response types ───────────────────────────────

interface CardTransactionRaw {
  id: string;
  cardId: string;
  walletId: string;
  userId: string;
  externalTxnId: string | null;
  type: string;
  status: string;
  amountCents: number;
  currency: string;
  merchantName: string | null;
  mcc: string | null;
  merchantCity: string | null;
  merchantCountry: string | null;
  createdAt: string;
}

interface WalletTransactionRaw {
  id: string;
  walletId: string;
  type: string;
  amountCents: number;
  balanceBefore: number;
  balanceAfter: number;
  reference: string | null;
  description: string | null;
  createdAt: string;
}

interface TopUpRaw {
  id: string;
  userId: string;
  walletId: string;
  payhubTopUpId: string | null;
  payhubTransactionId: string | null;
  bankReferenceNumber: string | null;
  amountPaise: number;
  currency: string;
  sourceType: string;
  status: string;
  createdAt: string;
}

// ── Unified row shape ────────────────────────────────────

interface UnifiedTransaction {
  id: string;
  source: TransactionSource;
  date: string;
  amountCents: number;
  currency: string;
  status: string;
  type: string;
  description: string;
  reference: string;
}

// ── Normalizers ──────────────────────────────────────────

function normalizeCardTransaction(txn: CardTransactionRaw): UnifiedTransaction {
  return {
    id: txn.id,
    source: "CARD",
    date: txn.createdAt,
    amountCents: txn.amountCents,
    currency: txn.currency || "AUD",
    status: txn.status,
    type: txn.type,
    description: txn.merchantName
      ? `${txn.merchantName}${txn.merchantCity ? `, ${txn.merchantCity}` : ""}`
      : `Card ${txn.type.toLowerCase()}`,
    reference: txn.externalTxnId || txn.id.slice(0, 8),
  };
}

function normalizeWalletTransaction(txn: WalletTransactionRaw): UnifiedTransaction {
  return {
    id: txn.id,
    source: "WALLET",
    date: txn.createdAt,
    amountCents: txn.amountCents,
    currency: "AUD",
    status: txn.type === "HOLD" ? "PENDING" : "SETTLED",
    type: txn.type,
    description: txn.description || `Wallet ${txn.type.toLowerCase()}`,
    reference: txn.reference || txn.id.slice(0, 8),
  };
}

function normalizeTopUp(txn: TopUpRaw): UnifiedTransaction {
  return {
    id: txn.id,
    source: "TOPUP",
    date: txn.createdAt,
    amountCents: txn.amountPaise,
    currency: txn.currency || "INR",
    status: txn.status,
    type: txn.sourceType || "TOPUP",
    description: `Top-up via ${txn.sourceType || "PayHub"}`,
    reference: txn.payhubTransactionId || txn.bankReferenceNumber || txn.id.slice(0, 8),
  };
}

// ── Helpers ──────────────────────────────────────────────

function getStatusColor(status: string | null | undefined): "default" | "secondary" | "destructive" | "outline" {
  if (!status) return "secondary";
  const statusColors: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    SUCCESS: "default",
    COMPLETED: "default",
    SETTLED: "default",
    PENDING: "outline",
    AWAITING_PAYMENT: "outline",
    AUTHORIZED: "outline",
    INITIATED: "secondary",
    FAILED: "destructive",
    DECLINED: "destructive",
    EXPIRED: "secondary",
    REFUNDED: "secondary",
    REVERSED: "secondary",
  };
  return statusColors[status] || "secondary";
}

function getSourceBadgeClass(source: TransactionSource): string {
  switch (source) {
    case "CARD": return "border-blue-300 text-blue-700 bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:bg-blue-950/40";
    case "WALLET": return "border-green-300 text-green-700 bg-green-50 dark:border-green-700 dark:text-green-400 dark:bg-green-950/40";
    case "TOPUP": return "border-purple-300 text-purple-700 bg-purple-50 dark:border-purple-700 dark:text-purple-400 dark:bg-purple-950/40";
  }
}

function getSourceLabel(source: TransactionSource): string {
  switch (source) {
    case "CARD": return "Card";
    case "WALLET": return "Wallet";
    case "TOPUP": return "Top-up";
  }
}

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return "-";
  }
}

function formatTime(dateString: string | null | undefined): string {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return "";
  }
}

// ── Page Component ───────────────────────────────────────

export default function Transactions() {
  const { formatCurrency } = useOrgSettings();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterSource, setFilterSource] = useState<string>("all");

  // Fetch all 3 sources in parallel
  const { data: cardTxns, isLoading: cardLoading } = useQuery<CardTransactionRaw[]>({
    queryKey: ["card-transactions"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/reports/transactions");
      if (!response.ok) throw new Error("Failed to fetch card transactions");
      return response.json();
    },
  });

  const { data: walletTxns, isLoading: walletLoading } = useQuery<WalletTransactionRaw[]>({
    queryKey: ["wallet-transactions"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/wallet-transactions");
      if (!response.ok) throw new Error("Failed to fetch wallet transactions");
      return response.json();
    },
  });

  const { data: topups, isLoading: topupsLoading } = useQuery<TopUpRaw[]>({
    queryKey: ["topups"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/topups");
      if (!response.ok) throw new Error("Failed to fetch top-ups");
      return response.json();
    },
  });

  const isLoading = cardLoading || walletLoading || topupsLoading;

  // Merge and sort all transactions
  const allTransactions = useMemo(() => {
    const normalized: UnifiedTransaction[] = [
      ...(cardTxns?.map(normalizeCardTransaction) || []),
      ...(walletTxns?.map(normalizeWalletTransaction) || []),
      ...(topups?.map(normalizeTopUp) || []),
    ];
    return normalized.sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [cardTxns, walletTxns, topups]);

  // Stats
  const stats = useMemo(() => ({
    totalTransactions: allTransactions.length,
    totalVolume: allTransactions.reduce((sum, t) => sum + t.amountCents, 0),
    successfulCount: allTransactions.filter(t =>
      ["SUCCESS", "SETTLED", "COMPLETED"].includes(t.status)
    ).length,
    pendingCount: allTransactions.filter(t =>
      ["PENDING", "AWAITING_PAYMENT", "AUTHORIZED", "INITIATED"].includes(t.status)
    ).length,
  }), [allTransactions]);

  // Filter
  const filteredTransactions = useMemo(() => {
    return allTransactions.filter((txn) => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = searchTerm === "" ||
        txn.description.toLowerCase().includes(searchLower) ||
        txn.reference.toLowerCase().includes(searchLower) ||
        txn.type.toLowerCase().includes(searchLower) ||
        txn.id.toLowerCase().includes(searchLower);

      const matchesStatus = filterStatus === "all" || txn.status === filterStatus;
      const matchesSource = filterSource === "all" || txn.source === filterSource;

      return matchesSearch && matchesStatus && matchesSource;
    });
  }, [allTransactions, searchTerm, filterStatus, filterSource]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Transactions</h1>
        <p className="text-muted-foreground mt-1">
          View all card, wallet, and top-up activity across the platform
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : (
              <>
                <div className="text-2xl font-bold">{stats.totalTransactions}</div>
                <p className="text-xs text-muted-foreground">
                  {cardTxns?.length || 0} card, {walletTxns?.length || 0} wallet, {topups?.length || 0} top-up
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Volume</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-24" /> : (
              <>
                <div className="text-2xl font-bold">{formatCurrency(stats.totalVolume)}</div>
                <p className="text-xs text-muted-foreground">All sources</p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Successful</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-16" /> : (
              <>
                <div className="text-2xl font-bold text-green-600">{stats.successfulCount}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.totalTransactions > 0
                    ? `${((stats.successfulCount / stats.totalTransactions) * 100).toFixed(1)}% success rate`
                    : "No data"}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-16" /> : (
              <>
                <div className="text-2xl font-bold text-orange-600">{stats.pendingCount}</div>
                <p className="text-xs text-muted-foreground">Awaiting completion</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Transactions</CardTitle>
          <CardDescription>Search and filter across all transaction sources</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <Select value={filterSource} onValueChange={setFilterSource}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="CARD">Card Transactions</SelectItem>
                <SelectItem value="WALLET">Wallet Operations</SelectItem>
                <SelectItem value="TOPUP">PayHub Top-ups</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="SETTLED">Settled</SelectItem>
                <SelectItem value="SUCCESS">Success</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="AUTHORIZED">Authorized</SelectItem>
                <SelectItem value="DECLINED">Declined</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="REVERSED">Reversed</SelectItem>
                <SelectItem value="REFUNDED">Refunded</SelectItem>
                <SelectItem value="EXPIRED">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Transactions ({filteredTransactions.length})</CardTitle>
          <CardDescription>Card, wallet, and top-up transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredTransactions.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map((txn) => (
                    <TableRow key={`${txn.source}-${txn.id}`}>
                      <TableCell className="text-sm">
                        <div className="flex flex-col">
                          <span className="font-medium">{formatDate(txn.date)}</span>
                          <span className="text-xs text-muted-foreground">{formatTime(txn.date)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn("text-xs", getSourceBadgeClass(txn.source))}>
                          <span className="flex items-center gap-1">
                            {txn.source === "CARD" && <CreditCard className="h-3 w-3" />}
                            {txn.source === "WALLET" && <Wallet className="h-3 w-3" />}
                            {txn.source === "TOPUP" && <Receipt className="h-3 w-3" />}
                            {getSourceLabel(txn.source)}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono">{txn.type}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{txn.description}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[120px] truncate">
                        {txn.reference}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(txn.amountCents)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(txn.status)}>
                          {txn.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No transactions found</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                {searchTerm || filterStatus !== "all" || filterSource !== "all"
                  ? "Try adjusting your filters"
                  : "Transactions will appear here once activity begins"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
